import React, { useState, useEffect } from 'react';
import { Table, Button, Form, Modal, Alert, Spinner } from 'react-bootstrap';
import pautaReajusteService from '../../services/pautaReajusteService';
import useCustomModal from '../../hooks/useCustomModal';
import CustomModal from '../CustomModal';
import MenuAccionesFila from './MenuAccionesFila';
import PanelFiltros, { CampoFiltro } from '../PanelFiltros';
import '../../style/components/GestionDatosMaestros.css';
import { coincideBusqueda } from '../../utils/textoComparable';

const TIPO_LABEL = { FALLO: 'Fallo', INDICE: 'Índice / Pauta' };

const GestionPautasReajuste = () => {
    const { modalState, hideModal, showModal } = useCustomModal();
    const [pautas, setPautas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModalForm, setShowModalForm] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [selectedPauta, setSelectedPauta] = useState(null);
    const [formData, setFormData] = useState({ nombre: '', tipo: 'FALLO', activo: true });
    const [alert, setAlert] = useState({ show: false, message: '', variant: '' });
    const [filterNombre, setFilterNombre] = useState('');

    useEffect(() => { cargarPautas(); }, []);

    const cargarPautas = async () => {
        setLoading(true);
        try {
            const data = await pautaReajusteService.getPautasReajuste(false);
            setPautas(Array.isArray(data) ? data : []);
        } catch {
            mostrarAlerta('Error al cargar el catálogo de pautas/fallos', 'danger');
        } finally {
            setLoading(false);
        }
    };

    const mostrarAlerta = (message, variant) => {
        setAlert({ show: true, message, variant });
        setTimeout(() => setAlert({ show: false, message: '', variant: '' }), 4000);
    };

    const handleOpenModal = (mode, pauta = null) => {
        setModalMode(mode);
        if (mode === 'edit' && pauta) {
            setSelectedPauta(pauta);
            setFormData({ nombre: pauta.nombre, tipo: pauta.tipo, activo: !!pauta.activo });
        } else {
            setSelectedPauta(null);
            setFormData({ nombre: '', tipo: 'FALLO', activo: true });
        }
        setShowModalForm(true);
    };

    const handleCloseModal = () => {
        setShowModalForm(false);
        setSelectedPauta(null);
        setFormData({ nombre: '', tipo: 'FALLO', activo: true });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (modalMode === 'create') {
                await pautaReajusteService.crearPautaReajuste(formData);
                mostrarAlerta('Pauta creada exitosamente', 'success');
            } else {
                await pautaReajusteService.actualizarPautaReajuste(selectedPauta.id, formData);
                mostrarAlerta('Pauta actualizada exitosamente', 'success');
            }
            handleCloseModal();
            cargarPautas();
        } catch {
            mostrarAlerta('Error al guardar la pauta', 'danger');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = (pauta) => {
        showModal({
            title: 'Eliminar pauta',
            type: 'warning',
            showCancel: true,
            confirmText: 'Sí, eliminar',
            cancelText: 'Cancelar',
            confirmVariant: 'warning',
            message: `¿Seguro que querés eliminar "${pauta.nombre}" del catálogo?`,
            onConfirm: async () => {
                try {
                    await pautaReajusteService.eliminarPautaReajuste(pauta.id);
                    mostrarAlerta('Pauta eliminada exitosamente', 'success');
                    cargarPautas();
                } catch {
                    mostrarAlerta('Error al eliminar la pauta', 'danger');
                }
            },
        });
    };

    const pautasFiltradas = pautas.filter(p => coincideBusqueda(p.nombre, filterNombre));

    return (
        <div className="gestion-datos-maestros">
            {alert.show && (
                <Alert variant={alert.variant} dismissible onClose={() => setAlert({ show: false, message: '', variant: '' })}>
                    {alert.message}
                </Alert>
            )}

            <div className="d-flex justify-content-end flex-wrap gap-2 mb-3">
                <Button variant="primary" onClick={() => handleOpenModal('create')}>
                    <i className="bi bi-plus-circle me-2"></i>
                    Nueva Pauta
                </Button>
            </div>

            <PanelFiltros
                hayFiltros={filterNombre.trim() !== ''}
                onLimpiar={() => setFilterNombre('')}
                resumen={`Mostrando ${pautasFiltradas.length} de ${pautas.length} pautas`}
            >
                <CampoFiltro etiqueta="Buscar" icono="bi-search" htmlFor="filtro-pauta-nombre" ancho>
                    <Form.Control
                        id="filtro-pauta-nombre"
                        className="panel-filtros-input"
                        type="text"
                        placeholder="Nombre del fallo o índice…"
                        value={filterNombre}
                        onChange={(e) => setFilterNombre(e.target.value)}
                    />
                </CampoFiltro>
            </PanelFiltros>

            {loading && pautas.length === 0 ? (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                </div>
            ) : (
                <div className="table-responsive">
                    <Table hover className="datos-maestros-table">
                        <thead>
                        <tr>
                            <th>Nombre</th>
                            <th>Tipo</th>
                            <th>Activo</th>
                            <th className="text-end col-acciones"><span className="visually-hidden">Acciones</span></th>
                        </tr>
                        </thead>
                        <tbody>
                        {pautasFiltradas.length === 0 ? (
                            <tr>
                                <td colSpan="4" className="text-center text-muted py-4">
                                    {pautas.length === 0
                                        ? 'No hay pautas registradas'
                                        : 'No se encontraron pautas que coincidan con la búsqueda'}
                                </td>
                            </tr>
                        ) : (
                            pautasFiltradas.map((pauta) => (
                                <tr key={pauta.id}>
                                    <td className="fw-semibold">{pauta.nombre}</td>
                                    <td>
                                        <span className={`badge ${pauta.tipo === 'FALLO' ? 'bg-primary' : 'bg-info text-dark'}`}>
                                            {TIPO_LABEL[pauta.tipo] || pauta.tipo}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`badge ${pauta.activo ? 'bg-success' : 'bg-secondary'}`}>
                                            {pauta.activo ? 'Activo' : 'Inactivo'}
                                        </span>
                                    </td>
                                    <td className="text-end">
                                        <MenuAccionesFila acciones={[
                                            { etiqueta: 'Editar', icono: 'bi-pencil', onClick: () => handleOpenModal('edit', pauta) },
                                            { etiqueta: 'Eliminar', icono: 'bi-trash', peligro: true, onClick: () => handleDelete(pauta) },
                                        ]} />
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </Table>
                </div>
            )}

            <Modal show={showModalForm} onHide={handleCloseModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {modalMode === 'create' ? 'Nueva Pauta' : 'Editar Pauta'}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Nombre *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.nombre}
                                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                                required
                                placeholder="Ej: Badaró"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Tipo *</Form.Label>
                            <Form.Select
                                value={formData.tipo}
                                onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                            >
                                <option value="FALLO">Fallo</option>
                                <option value="INDICE">Índice / Pauta</option>
                            </Form.Select>
                        </Form.Group>

                        <Form.Check
                            type="checkbox"
                            id="pauta-activo"
                            label="Activo"
                            checked={formData.activo}
                            onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                        />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>
                            Cancelar
                        </Button>
                        <Button variant="primary" type="submit" disabled={loading}>
                            {loading ? <Spinner animation="border" size="sm" /> : 'Guardar'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <CustomModal {...modalState} onHide={hideModal} />
        </div>
    );
};

export default GestionPautasReajuste;
